// Topbar (Parte 14.2 + Design Tipográfico Limpo GIYUS):
// No canto superior direito fica exclusivamente o Usuário Logado e o menu com todas as Opções do Usuário Logado
// (Configurações do Usuário /me, Caixa de Entrada, Tema Claro/Escuro, Idioma, Escopo Holding ↔ Tenants, Conversor PTAX, Microsoft Entra ID SSO e Sair).
import { type Locale, SUPPORTED_LOCALES } from '@giyus/i18n';
import {
  BrandLogo,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  NativeSelect,
} from '@giyus/ui';
import { MenuIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { useAuth, useMe } from '../lib/auth/authContext';
import { ROUTES } from '../lib/context/contextResolver';
import { useActiveContext } from '../lib/context/useActiveContext';
import { THEMES, type Theme, useTheme } from '../lib/theme/theme';
import { useInboxCounters } from '../modules/workflow/api/queries';
import { ContextSelector } from './ContextSelector';
import { EntraAccessPortal, startMicrosoftEntraOidcRedirect } from './EntraAccessPortal';
import { InboxButton } from './InboxPanel';

export interface TopbarProps {
  readonly sidebarOpen?: boolean;
  readonly onToggleSidebar?: () => void;
  readonly showContextSelector: boolean;
}

function getInitials(displayName: string, email: string): string {
  const trimmed = displayName.trim();
  if (trimmed) {
    const parts = trimmed.split(/\s+/);
    const first = parts[0]?.[0] ?? '';
    const second = parts[1]?.[0] ?? '';
    const combined = `${first}${second}`.toUpperCase();
    if (combined) {
      return combined;
    }
  }
  return email.slice(0, 2).toUpperCase();
}

export function Topbar({ onToggleSidebar, showContextSelector, sidebarOpen = false }: TopbarProps) {
  const { t, i18n } = useTranslation(['shell', 'common']);
  const me = useMe();
  const { signOut } = useAuth();
  const { theme, setTheme } = useTheme();
  const { data: inboxCounters } = useInboxCounters();
  const inboxCount = inboxCounters?.total ?? 0;
  const {
    hasNetworkAccess,
    isNetworkScope,
    context,
    site,
    contexts,
    switchTenant,
    selectSite,
    goToNetwork,
  } = useActiveContext();
  const [entraModalOpen, setEntraModalOpen] = useState(false);
  const [inboxDrawerOpen, setInboxDrawerOpen] = useState(false);
  const [ptaxBarOpen, setPtaxBarOpen] = useState(false);
  const [ptaxRate, setPtaxRate] = useState(5.7);
  const [selicRate] = useState(14.25);
  const [giroPjRate] = useState(27.64);
  const [quickAmount, setQuickAmount] = useState('1000');
  const [quickDir, setQuickDir] = useState<'USD_TO_BRL' | 'BRL_TO_USD'>('USD_TO_BRL');
  const currentLocale = i18n.resolvedLanguage ?? i18n.language;

  const numericQuickAmt = Math.max(0, Number(quickAmount) || 0);
  const quickConvertedBrl = numericQuickAmt * ptaxRate;
  const quickConvertedUsd = ptaxRate > 0 ? numericQuickAmt / ptaxRate : 0;

  const role =
    (isNetworkScope ? me.networkAccess.roles[0] : context?.effectiveRoles[0]?.role) ?? '';
  const roleLabel = role ? t(`shell:roles.${role}`, { defaultValue: role }) : '';
  const initials = getInitials(me.user.displayName, me.user.email);
  const homeHref = isNetworkScope
    ? ROUTES.network
    : context
      ? ROUTES.tenant(context.code)
      : hasNetworkAccess
        ? ROUTES.network
        : contexts[0]
          ? ROUTES.tenant(contexts[0].code)
          : '/';

  return (
    <header className="giyus-topbar sticky top-0 z-30" data-testid="topbar">
      <div className="flex flex-wrap items-center justify-between gap-3 px-(--page-gutter) py-3">
        <div className="flex min-w-0 max-w-full items-center gap-3">
          {onToggleSidebar && (
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden min-h-(--control-h-sm)"
              aria-label={t('common:a11y.toggleSidebar')}
              id="sidebar-toggle"
              aria-controls="mobile-sidebar"
              aria-expanded={sidebarOpen}
              onClick={onToggleSidebar}
            >
              <MenuIcon aria-hidden="true" />
            </Button>
          )}

          <Link
            to={homeHref}
            aria-label={t('shell:nav.home')}
            className="inline-flex items-center gap-2 text-title font-semibold text-heading no-underline hover:opacity-90"
            data-testid="topbar-brand-home-link"
          >
            <BrandLogo symbol className="size-6 shrink-0" />
            {!showContextSelector && <span>GIYUS</span>}
          </Link>

          {showContextSelector && <ContextSelector />}
        </div>

        {/* Canto Superior Direito: Exclusivamente o Usuário Logado e as Opções do Usuário Logado */}
        <div className="ml-auto flex min-w-0 max-w-full items-center gap-2">
          <InboxButton showTrigger open={inboxDrawerOpen} onOpenChange={setInboxDrawerOpen} />

          {/* Menu Único do Usuário Logado (Canto Superior Direito) */}
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                type="button"
                aria-label={t('common:a11y.userMenu')}
                className="giyus-user-trigger min-w-0 max-w-full shrink min-h-(--control-h-sm)"
                data-testid="user-menu"
              >
                <span className="giyus-avatar-circle" aria-hidden="true">
                  {initials}
                </span>
                <span
                  className="flex min-w-0 flex-col items-start leading-tight"
                  data-testid="topbar-user"
                >
                  <span className="max-w-full truncate text-body-sm sm:max-w-40 font-semibold text-heading">
                    {me.user.displayName}
                  </span>
                  <span className="max-w-full truncate text-caption sm:max-w-48 font-normal text-muted-foreground">
                    {roleLabel} · {isNetworkScope ? 'Holding' : context?.code}
                  </span>
                  <span className="sr-only">{me.user.email}</span>
                </span>
                <span className="text-caption opacity-80" aria-hidden="true">
                  ▾
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-75 max-w-[94vw] rounded-card border-border-strong p-1.5 shadow-modal"
            >
              <DropdownMenuLabel className="p-0">
                <div className="flex items-center gap-2.5 rounded-card bg-surface-sunken p-2.5">
                  <span className="giyus-avatar-circle size-9 text-body" aria-hidden="true">
                    {initials}
                  </span>
                  <div className="flex min-w-0 flex-col gap-0.5">
                    <span className="truncate text-body font-semibold text-heading">
                      {me.user.displayName}
                    </span>
                    <span className="truncate text-caption font-normal text-muted-foreground">
                      {me.user.email}
                    </span>
                    <div className="mt-0.5 flex flex-wrap gap-1">
                      <span className="rounded-pill bg-accent px-2 text-caption font-bold text-accent-foreground">
                        {roleLabel}
                      </span>
                      <span className="rounded-pill border border-border-strong px-2 text-caption text-heading">
                        {isNetworkScope ? 'Holding' : context?.code}
                      </span>
                    </div>
                  </div>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuLabel className="px-2.5 pt-3 text-caption font-bold tracking-wider text-muted-foreground uppercase">
                {t('shell:topbar.myAccount')}
              </DropdownMenuLabel>
              <DropdownMenuItem asChild>
                <Link to={ROUTES.account}>{t('shell:topbar.profile')}</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/me/person" data-testid="user-menu-my-registration-link">
                  Meus dados cadastrais
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger data-testid="user-menu-settings-submenu">
                  {t('shell:topbar.settings')}
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="max-h-96 max-w-80 overflow-y-auto">
                  <DropdownMenuItem asChild>
                    <Link to={ROUTES.account} data-testid="user-menu-account-settings-link">
                      {t('shell:topbar.settings')}
                    </Link>
                  </DropdownMenuItem>{' '}
                  {/* SEÇÃO 3: Contexto Operacional (Holding GIYUS Controla Todos os Centros de Pesquisa GIYUS) */}
                  <DropdownMenuLabel className="text-caption font-bold tracking-wider text-heading uppercase">
                    Contexto Operacional (Holding GIYUS &amp; Centros GIYUS)
                  </DropdownMenuLabel>
                  <div className="px-2 pb-1.5 text-caption leading-snug text-muted-foreground">
                    Um Tenant é um <strong>Centro de Pesquisa GIYUS (Não-Holding)</strong>. A{' '}
                    <strong>GIYUS Holding</strong> (única) controla todos os Centros de Pesquisa
                    GIYUS.
                  </div>
                  {hasNetworkAccess && (
                    <DropdownMenuItem
                      onSelect={() => {
                        goToNetwork();
                      }}
                      data-testid="user-menu-context-network"
                    >
                      <div className="flex w-full items-center justify-between gap-2">
                        <span className="font-semibold">
                          Holding GIYUS (Serviços Compartilhados)
                        </span>
                        {isNetworkScope && (
                          <span className="rounded-control bg-primary px-2 py-0.5 text-caption font-bold text-primary-foreground">
                            ATIVO
                          </span>
                        )}
                      </div>
                    </DropdownMenuItem>
                  )}
                  {contexts.map((item) => {
                    const isTenantActive = !isNetworkScope && context?.code === item.code;
                    return (
                      <DropdownMenuItem
                        key={item.tenantId}
                        onSelect={() => {
                          switchTenant(item.code);
                        }}
                        data-testid={`user-menu-context-${item.code}`}
                      >
                        <div className="flex w-full items-center justify-between gap-2">
                          <span className="flex flex-col">
                            <span className="font-semibold">
                              {item.name} ({item.code})
                            </span>
                            <span className="text-caption text-muted-foreground">
                              {t('shell:home.studyCount', { count: item.accessibleStudySiteCount })}
                            </span>
                          </span>
                          {isTenantActive && (
                            <span className="rounded-control bg-link px-2 py-0.5 text-caption font-bold text-foreground">
                              ATIVO
                            </span>
                          )}
                        </div>
                      </DropdownMenuItem>
                    );
                  })}
                  {!isNetworkScope && context && context.sites.length > 1 && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-caption font-bold text-muted-foreground">
                        Unidade / Site em {context.name}
                      </DropdownMenuLabel>
                      <DropdownMenuItem
                        onSelect={() => {
                          selectSite(null);
                        }}
                        data-testid="user-menu-site-all"
                      >
                        <div className="flex w-full items-center justify-between gap-2 text-caption">
                          <span>{t('shell:context.allSites')}</span>
                          {site === null && (
                            <span className="rounded bg-surface-sunken px-1.5 py-0.5 text-caption font-bold text-accent-foreground">
                              ATIVO
                            </span>
                          )}
                        </div>
                      </DropdownMenuItem>
                      {context.sites.map((s) => (
                        <DropdownMenuItem
                          key={s.id}
                          onSelect={() => {
                            selectSite(s.id);
                          }}
                          data-testid={`user-menu-site-${s.id}`}
                        >
                          <div className="flex w-full items-center justify-between gap-2 text-caption">
                            <span>
                              {s.name} ({s.timeZoneId})
                            </span>
                            {site?.id === s.id && (
                              <span className="rounded bg-surface-sunken px-1.5 py-0.5 text-caption font-bold text-accent-foreground">
                                ATIVO
                              </span>
                            )}
                          </div>
                        </DropdownMenuItem>
                      ))}
                    </>
                  )}
                  <DropdownMenuSeparator />
                  {/* SEÇÃO 4: Ferramentas Rápidas & Sessão Microsoft Entra ID */}
                  <DropdownMenuLabel className="text-caption font-bold tracking-wider text-heading uppercase">
                    Ferramentas &amp; Sessão Microsoft Entra ID
                  </DropdownMenuLabel>
                  <DropdownMenuItem
                    onSelect={() => {
                      setPtaxBarOpen((v) => !v);
                    }}
                    data-testid="user-menu-ptax-toggle"
                  >
                    <div className="flex w-full items-center justify-between gap-2">
                      <span>Conversor Cambial BCB PTAX &amp; Selic</span>
                      <span className="rounded bg-success-subtle px-1.5 py-0.5 font-mono text-caption font-bold text-success-subtle-foreground">
                        R$ {ptaxRate.toFixed(4)}
                      </span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => {
                      void startMicrosoftEntraOidcRedirect(me.user.email);
                    }}
                    data-testid="user-menu-entra-sso"
                  >
                    <span>Autenticar no Microsoft Entra ID (login.microsoftonline.com)</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => {
                      setEntraModalOpen(true);
                    }}
                  >
                    <span>Abrir Portal de Acesso (@giyus.com.br / Outros @)</span>
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              <DropdownMenuItem asChild>
                <Link to="/me/preferences/notifications">{t('shell:topbar.notifications')}</Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-caption font-bold tracking-wider text-muted-foreground uppercase">
                {t('shell:topbar.pending')}
              </DropdownMenuLabel>
              <DropdownMenuItem
                onSelect={() => setInboxDrawerOpen(true)}
                data-testid="user-menu-inbox-option"
              >
                <span className="flex w-full justify-between gap-2">
                  {t('shell:topbar.tasks')}
                  <span className="font-data font-semibold text-heading">
                    {inboxCounters ? inboxCount : '—'}
                  </span>
                </span>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/me/delegations">{t('shell:topbar.delegations')}</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/me/access-reviews">{t('shell:topbar.accessReviews')}</Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-caption font-bold tracking-wider text-muted-foreground uppercase">
                {t('shell:topbar.appearance')}
              </DropdownMenuLabel>
              <DropdownMenuRadioGroup
                value={theme}
                onValueChange={(value) => {
                  setTheme(value as Theme);
                }}
                aria-label={t('common:theme.label')}
                className="grid grid-cols-2 gap-1 px-2.5 py-1"
              >
                {THEMES.filter((item) => item !== 'system').map((item: Theme) => (
                  <DropdownMenuRadioItem
                    key={item}
                    value={item}
                    onSelect={(event) => event.preventDefault()}
                    data-testid={`user-menu-theme-${item}`}
                  >
                    {t(`common:theme.${item}`)}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
              <DropdownMenuRadioGroup
                value={currentLocale}
                onValueChange={(value) => {
                  void i18n.changeLanguage(value as Locale);
                }}
                aria-label={t('common:language.label')}
                className="grid grid-cols-2 gap-1 px-2.5 py-1"
              >
                {SUPPORTED_LOCALES.map((locale: Locale) => (
                  <DropdownMenuRadioItem
                    key={locale}
                    value={locale}
                    onSelect={(event) => event.preventDefault()}
                    data-testid={`user-menu-locale-${locale}`}
                  >
                    {t(`common:language.${locale}`)}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() => {
                  void signOut();
                }}
                className="font-semibold text-danger-subtle-foreground"
              >
                <span>{t('common:actions.signOut')}</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {ptaxBarOpen ? (
        <div
          className="flex flex-wrap items-center justify-between gap-3 border-t border-info-border bg-primary px-6 py-2.5 text-caption text-primary-foreground"
          data-testid="topbar-ptax-converter-bar"
        >
          <div className="flex flex-wrap items-center gap-3">
            <span className="rounded bg-info-subtle px-2.5 py-0.5 font-mono text-caption font-bold text-info-subtle-foreground">
              Base Diária BCB PTAX &amp; Conversor Universal (R$ ↔ USD c/ Overhead 30%)
            </span>
            <span>
              Venda: <strong>R$ {ptaxRate.toFixed(4)}</strong>
            </span>
            <span>
              Selic: <strong>{selicRate.toFixed(2)}% a.a.</strong>
            </span>
            <span>
              Capital de Giro PJ: <strong>{giroPjRate.toFixed(2)}% a.a.</strong>
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-1.5 text-caption">
              <span>Valor:</span>
              <Input
                type="number"
                aria-label="Valor para conversão PTAX"
                value={quickAmount}
                onChange={(e) => {
                  setQuickAmount(e.target.value);
                }}
                className="w-24 rounded border px-2 py-1 text-caption font-bold"
              />
            </label>
            <NativeSelect
              aria-label="Direção da conversão PTAX"
              value={quickDir}
              onChange={(e) => {
                setQuickDir(e.target.value as 'USD_TO_BRL' | 'BRL_TO_USD');
              }}
              className="rounded border px-2 py-1 text-caption font-bold"
            >
              <option value="USD_TO_BRL">USD → BRL (R$)</option>
              <option value="BRL_TO_USD">BRL (R$) → USD</option>
            </NativeSelect>
            <span
              className="rounded bg-success-subtle px-2.5 py-1 font-mono text-caption font-bold text-success-subtle-foreground"
              data-testid="topbar-ptax-converted-result"
            >
              {quickDir === 'USD_TO_BRL'
                ? `= R$ ${quickConvertedBrl.toFixed(2)} (c/ Overhead 30%: R$ ${(quickConvertedBrl * 1.3).toFixed(2)})`
                : `= USD ${quickConvertedUsd.toFixed(2)} (c/ Overhead 30%: USD ${(quickConvertedUsd * 1.3).toFixed(2)})`}
            </span>
            <Button
              variant="default"
              type="button"
              onClick={() => {
                setPtaxRate(5.7125);
              }}
              className="rounded px-2.5 py-1 text-caption font-bold min-h-(--control-h-sm)"
              data-testid="btn-sync-ptax-topbar"
            >
              Sincronizar BCB
            </Button>
          </div>
        </div>
      ) : null}

      {entraModalOpen ? (
        <EntraAccessPortal
          onClose={() => {
            setEntraModalOpen(false);
          }}
        />
      ) : null}
    </header>
  );
}
